import {
  Charge,
  ChargeStatus,
  ExportFormat,
  SortDirection,
} from '@/@types/generated'
import { axiosInstance } from '@/@types/generated/core/request'
import { ChargeSort } from '@/@types/generated/models/ChargeSort'
import { DataTablePagination } from '@/components/Atoms/DataTablePagination'
import { useAuth } from '@/hooks/useAuth'
import { PaginationState, SortingState } from '@tanstack/react-table'
import React, {
  createContext,
  use,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useSearchParams } from 'react-router'

interface ListChargesRequest {
  query?: string
  businessEntityId?: string
  status?: Array<ChargeStatus>
  createdAt?: string
  limit?: number
  offset?: number
  sortBy?: ChargeSort
  sortDirection?: SortDirection
}

interface ChargesContextType<TData = Charge, TRequest = ListChargesRequest> {
  readonly query: TRequest | null
  readonly sorting: SortingState
  readonly pagination: PaginationState
  readonly data: TData[]
  readonly isLoading: boolean
  readonly total: number

  refresh: () => Promise<void>
  exportCharges: (format: ExportFormat) => Promise<Response>
  setQuery: React.Dispatch<React.SetStateAction<TRequest>>
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>
}

const ChargesContext = createContext<ChargesContextType<any, any>>(undefined!)

export function useCharges<
  T = Charge,
  U = ListChargesRequest,
>(): ChargesContextType<T, U> {
  const context = use<ChargesContextType<T, U>>(ChargesContext)
  if (context === undefined) {
    throw new Error('useCharges must be used within a ChargesProvider')
  }
  return context
}

export interface ChargesProviderProps {
  // If true, persists query changes in the search parameters
  // If a string, uses the string as the key prefix in the search parameters
  persistQuery?: boolean | string
  listCharges: (
    query: ListChargesRequest
  ) => Promise<{ charges: Charge[]; total: number }>
  exportCharges?: (
    query: {
      query?: string
      status?: ChargeStatus[] | null
      createdAt?: string
      sortBy: ChargeSort | undefined
      sortDirection: SortDirection
    },
    format: ExportFormat
  ) => Promise<Response>
}

export function ChargesProvider({
  persistQuery = false,
  listCharges,
  exportCharges,
  children,
}: React.PropsWithChildren<ChargesProviderProps>) {
  // Hooks
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()

  // Get a search params persisted value, if enabled
  const getParam = (key: string): string | undefined => {
    if (!persistQuery) return undefined
    const param = searchParams.get(
      typeof persistQuery === 'string' ? `${persistQuery}:${key}` : key
    )
    return param ?? undefined
  }

  // State
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<Charge[]>([])
  const [total, setTotal] = useState(0)
  const [query, setQuery] = useState<ListChargesRequest>({
    query: getParam('quer'),
    businessEntityId: getParam('businessEntityId'),
    status: getParam('status')
      ?.split(',')
      .filter((s): s is ChargeStatus =>
        Object.values(ChargeStatus).includes(s as ChargeStatus)
      ),
    createdAt: getParam('createdAt'),
    limit: getParam('limit') ? Number(getParam('limit')) : undefined,
    offset: getParam('offset') ? Number(getParam('offset')) : undefined,
    sortBy: Object.values(ChargeSort).includes(getParam('sortBy') as ChargeSort)
      ? (getParam('sortBy') as ChargeSort)
      : undefined,
    sortDirection: Object.values(SortDirection).includes(
      getParam('sortDirection') as SortDirection
    )
      ? (getParam('sortDirection') as SortDirection)
      : undefined,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: ChargeSort.CHARGE_DATE, desc: true },
  ])
  const [pagination, setPagination] = useState<PaginationState>(
    DataTablePagination.DefaultState()
  )

  // List charges when query changes
  const _listCharges = useCallback(async () => {
    try {
      setIsLoading(true)
      const res = await listCharges({
        ...query,

        // Managed pagination and sorting
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        sortBy: sorting[0]?.id as ChargeSort | undefined,
        sortDirection: sorting[0]?.desc
          ? SortDirection.DESC
          : SortDirection.ASC,
      })

      setData(res.charges ?? [])
      setTotal(res.total)

      return res
    } finally {
      setIsLoading(false)
    }
  }, [listCharges, query, pagination.pageSize, pagination.pageIndex, sorting])

  // Refresh charges
  const refresh = useCallback(async () => {
    await _listCharges()
  }, [_listCharges])

  // Load charges when the query changes, debounced
  useEffect(() => {
    if (!user) return
    const timeout = setTimeout(() => _listCharges(), 300)
    return () => clearTimeout(timeout)
  }, [_listCharges, user])

  // When filters (status, businessEntityId, etc.) change, reset pagination
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [query])

  const _exportCharges = useCallback(
    async (format: ExportFormat) => {
      if (!exportCharges) throw new Error('Export function is not provided')
      return await exportCharges(
        {
          ...query,
          sortBy: sorting[0]?.id as ChargeSort | undefined,
          sortDirection: sorting[0]?.desc
            ? SortDirection.DESC
            : SortDirection.ASC,
        },
        format
      )
    },
    [exportCharges, query, sorting]
  )

  // Persist filters to the URL
  useEffect(() => {
    // Persist once records are loaded
    if (isLoading) return
    if (!persistQuery) return

    setSearchParams(
      (sp) => {
        const set = (k: string, v?: string | null) => {
          if (v) sp.set(k, v)
          else sp.delete(k)
        }

        set('query', query.query)
        set('businessEntityId', query.businessEntityId)
        set('status', query.status?.join(','))

        return sp
      },
      { preventScrollReset: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  return (
    <ChargesContext.Provider
      value={{
        // Data
        query,
        data,
        isLoading,
        total,
        sorting,
        pagination,
        // State methods
        setQuery,
        setPagination,
        setSorting,
        // Methods
        refresh,
        exportCharges: _exportCharges,
      }}
    >
      {children}
    </ChargesContext.Provider>
  )
}

export const defaultExportCharges: (
  baseEndpoint: string,
  businessEntityId?: string
) => NonNullable<ChargesProviderProps['exportCharges']> =
  (baseEndpoint, businessEntityId) =>
  async ({ query, status, createdAt, sortBy, sortDirection }, format) => {
    const axiosResp = await axiosInstance.get(baseEndpoint, {
      params: {
        format,
        ...(businessEntityId !== undefined && { businessEntityId }),
        ...(query !== undefined && { query }),
        ...(status !== undefined && { status }),
        ...(createdAt !== undefined && { createdAt }),
        ...(sortBy && { sortBy }),
        ...(sortDirection && { sortDirection }),
      },
      paramsSerializer: {
        indexes: null,
      },
      responseType: 'blob',
    })
    return new Response(axiosResp.data, {
      status: axiosResp.status,
      headers: new Headers({
        'Content-Disposition': String(
          axiosResp.headers['content-disposition'] ?? ''
        ),
        'Content-Type': String(axiosResp.headers['content-type'] ?? ''),
      }),
    })
  }
